/**
 * NAO checkout — selector de aromas con cantidad por aroma (roll-on).
 *
 * En estos productos la cantidad del pedido NO sale de un input único: es la
 * suma de las líneas elegidas, y cada línea viaja al backend como un elemento
 * de `items[]` para que la orden de Shopify salga con una línea por aroma. Eso
 * es lo que hace que quien empaca (y Dropi) vea exactamente qué mandar, sin
 * tener que confirmar nada por WhatsApp.
 *
 * Este módulo solo lee y escribe el DOM del selector; no calcula envíos ni
 * totales. El recálculo lo dispara el `onChange` que recibe `setupAromas`, que
 * es el mismo `recalculate` del resto del checkout.
 *
 * Recibe el contexto compartido (`ctx`) que arma el entry (nao-checkout.js).
 */

// Tope del pedido entero, no de cada aroma: 20 unidades repartidas como sea.
// Coincide con MAX_QUANTITY del backend, que rechaza con 400 si se pasa.
export const MAX_TOTAL = 20;

// ¿Este checkout usa selector de aromas? Sirve para que el resto del código no
// tenga que mirar la config: si el markup no está, no hay aromas.
export function hasAromas(ctx) {
    return !!(ctx.elements.aromaRows && ctx.elements.aromaRows.length);
}

/**
 * Líneas elegidas, en el orden en que aparecen en pantalla y sin los aromas en
 * cero. Es la única lectura del selector: el resumen, el total y el payload
 * salen todos de aquí, así que no pueden discrepar entre sí.
 */
export function aromaLines(ctx) {

    const lines = [];

    if (!hasAromas(ctx)) return lines;

    ctx.elements.aromaRows.forEach(row => {

        const input = row.querySelector('.js-nao-aroma-qty');

        if (!input) return;

        const quantity = Math.max(0, parseInt(input.value, 10) || 0);

        if (quantity === 0) return;

        lines.push({
            variantId: row.dataset.variantId || '',
            title: row.dataset.title || '',
            // Los precios de Shopify vienen en centavos, como el data-price del
            // resumen; aquí se normalizan a pesos igual que PRODUCT_PRICE.
            price: (parseInt(row.dataset.price, 10) || 0) / 100,
            quantity
        });
    });

    return lines;
}

export function aromaTotal(ctx) {
    return aromaLines(ctx).reduce((sum, line) => sum + line.quantity, 0);
}

// Subtotal real: cada aroma podría tener su propio precio, así que no se
// multiplica un precio único por la cantidad.
export function aromaSubtotal(ctx) {
    return aromaLines(ctx).reduce((sum, line) => sum + line.price * line.quantity, 0);
}

// "2 Lavanda · 1 Menta" para el resumen de solo lectura del formulario.
export function describeAromas(lines) {
    return lines.map(line => `${line.quantity} ${line.title}`).join(' · ');
}

// Normaliza el valor de una casilla: vacío o basura vuelve a 0, y nunca se pasa
// del tope del pedido contando lo que ya llevan los demás aromas.
function clamp(ctx, input, value) {

    const current = Math.max(0, parseInt(input.value, 10) || 0);
    const otros = aromaTotal(ctx) - current;
    const room = Math.max(0, MAX_TOTAL - otros);

    return Math.min(Math.max(0, value), room);
}

/**
 * Cablea los steppers. `onChange` se llama después de cada cambio ya aplicado
 * al DOM, para que quien recalcula lea siempre el estado definitivo.
 */
export function setupAromas(ctx, onChange) {

    if (!hasAromas(ctx)) return;

    ctx.elements.aromaRows.forEach(row => {

        const input = row.querySelector('.js-nao-aroma-qty');
        const minus = row.querySelector('.js-nao-aroma-minus');
        const plus = row.querySelector('.js-nao-aroma-plus');

        if (!input) return;

        const step = (delta) => {
            const current = Math.max(0, parseInt(input.value, 10) || 0);
            input.value = String(clamp(ctx, input, current + delta));
            onChange();
        };

        if (minus) minus.addEventListener('click', () => step(-1));
        if (plus) plus.addEventListener('click', () => step(1));

        // Escribir a mano recalcula en vivo; al salir del campo se sanea.
        input.addEventListener('input', onChange);

        input.addEventListener('change', () => {
            input.value = String(clamp(ctx, input, parseInt(input.value, 10) || 0));
            onChange();
        });
    });
}
